import { cn } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** Progress of one step; drives its status text and styling. */
export enum StepIndicatorStatus {
	NOT_STARTED = 'NOT_STARTED',
	IN_PROGRESS = 'IN_PROGRESS',
	COMPLETE = 'COMPLETE',
	SKIPPED = 'SKIPPED',
}

/**
 * One step.
 *
 * @property id - Stable identity, passed back to `onSelect`.
 * @property label - The step's name (product copy, supplied by the consumer).
 * @property status - Its progress.
 * @property optional - Shows the optional marker.
 * @property selectable - Whether the step can be opened; a non-selectable step is a disabled button.
 */
export interface StepIndicatorItem {
	id: string
	label: string
	status: StepIndicatorStatus
	optional: boolean
	selectable: boolean
}

/**
 * Built-in strings of {@link StepIndicator}, overridable per locale (tech §59).
 *
 * @property region - Accessible name of the navigation landmark. Default `Progress`.
 * @property optional - The optional marker. Default `Optional`.
 * @property status - The status text per {@link StepIndicatorStatus}.
 */
export interface StepIndicatorLabels {
	region: string
	optional: string
	status: Record<StepIndicatorStatus, string>
}

const DEFAULT_LABELS: StepIndicatorLabels = {
	region: 'Progress',
	optional: 'Optional',
	status: {
		[StepIndicatorStatus.NOT_STARTED]: 'Not started',
		[StepIndicatorStatus.IN_PROGRESS]: 'In progress',
		[StepIndicatorStatus.COMPLETE]: 'Complete',
		[StepIndicatorStatus.SKIPPED]: 'Skipped',
	},
}

/** Layout of the step list. Vertical (the default) is the sidebar stepper; horizontal is a top step bar. */
export type StepIndicatorOrientation = 'vertical' | 'horizontal'

/**
 * Props for the {@link StepIndicator} molecule.
 *
 * @property items - Steps in display order.
 * @property currentId - The open step's id (`aria-current="step"`).
 * @property onSelect - Called with a selectable step's id when it is pressed; omit for a read-only indicator.
 * @property orientation - Lays the steps out vertically (default) or as a horizontal bar (TO-652).
 * @property labels - Localised built-in strings; each omitted key falls back to English.
 */
export interface StepIndicatorProps {
	items: StepIndicatorItem[]
	currentId: string
	onSelect?: (id: string) => void
	orientation?: StepIndicatorOrientation
	labels?: Partial<Omit<StepIndicatorLabels, 'status'>> & { status?: Partial<StepIndicatorLabels['status']> }
}

/** No step selection wired: a read-only indicator still renders, activation is a no-op. */
function ignoreSelect(): void {
	// Intentionally empty — the default when a consumer passes no onSelect.
}

/** One step row: a status-styled button, disabled when the step is not selectable. */
function StepButton({
	item,
	index,
	current,
	optionalLabel,
	statusLabel,
	onSelect,
}: {
	item: StepIndicatorItem
	index: number
	current: boolean
	optionalLabel: string
	statusLabel: string
	onSelect: (id: string) => void
}): ReactElement {
	return (
		<li>
			<button
				type="button"
				aria-current={current ? 'step' : undefined}
				disabled={!item.selectable}
				onClick={() => onSelect(item.id)}
				className={cn(
					'flex w-full items-center gap-ds-2 rounded-ds-md border-l-4 bg-ds-surface-elevated p-3 text-left text-ds-sm text-ds-text',
					'focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-60',
					STATUS_CLASSES[item.status],
					current && 'font-medium',
				)}
			>
				<span aria-hidden="true" className="text-ds-text-muted">
					{index + 1}.
				</span>
				<span className="flex-1 min-w-0">
					<span className="block">{item.label}</span>
					{item.optional && <span className="block text-ds-text-muted"> {optionalLabel}</span>}
				</span>
				<span className="shrink-0 text-ds-text-muted"> {statusLabel}</span>
			</button>
		</li>
	)
}

const STATUS_CLASSES: Record<StepIndicatorStatus, string> = {
	[StepIndicatorStatus.NOT_STARTED]: 'border-ds-border',
	[StepIndicatorStatus.IN_PROGRESS]: 'border-ds-info',
	[StepIndicatorStatus.COMPLETE]: 'border-ds-success',
	[StepIndicatorStatus.SKIPPED]: 'border-ds-warning',
}

/**
 * An ordered list of steps with each step's status, for a multi-step flow.
 *
 * @remarks
 * - **Accessibility:** a `nav` landmark named `labels.region` wrapping an ordered
 *   list; each step is a button, the open one carries `aria-current="step"`, and a
 *   non-selectable step is a disabled button (not focusable, never calls `onSelect`).
 * - **Presentational only:** which steps are selectable is the consumer's rule.
 * - **Orientation:** vertical by default (the sidebar stepper); `horizontal` lays the
 *   steps in a wrapping row for a top step bar (TO-652 WizardShell).
 *
 * @see packages/ui-components/src/step-indicator/step-indicator.stories.tsx
 */
export function StepIndicator({
	items,
	currentId,
	onSelect = ignoreSelect,
	orientation = 'vertical',
	labels,
}: StepIndicatorProps): ReactElement {
	const text = {
		...DEFAULT_LABELS,
		...labels,
		status: { ...DEFAULT_LABELS.status, ...labels?.status },
	}
	const horizontal = orientation === 'horizontal'
	return (
		<nav aria-label={text.region}>
			<ol
				data-orientation={orientation}
				className={cn('flex gap-ds-2', horizontal ? 'flex-row flex-wrap' : 'flex-col')}
			>
				{items.map((item, index) => (
					<StepButton
						key={item.id}
						item={item}
						index={index}
						current={item.id === currentId}
						optionalLabel={text.optional}
						statusLabel={text.status[item.status]}
						onSelect={onSelect}
					/>
				))}
			</ol>
		</nav>
	)
}
